import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import type { TPoll } from '@onesource/schemas';
import { getMediaUrl, useGetPollVoters } from '@onesource/services';

import { PollVotersDialogSkeleton } from './poll-voters-dialog-skeleton';

interface PollVotersDialogProps {
  poll: TPoll;
  open: boolean;
  onClose: () => void;
}

/**
 * Lists who voted for each option of a poll, grouped by option. Fetches voters
 * only while open. Option order and labels come from the poll; counts fall back
 * to the poll's denormalized vote counts while the voter list loads.
 */
export const PollVotersDialog = ({ poll, open, onClose }: PollVotersDialogProps) => {
  const { data, isLoading } = useGetPollVoters(poll.id, open);

  const votersByOption = new Map(data?.options.map(option => [option.optionId, option.voters]) ?? []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle onClose={onClose}>Poll votes</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <PollVotersDialogSkeleton optionCount={poll.options.length} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {poll.options.map(option => {
              const voters = votersByOption.get(option.id) ?? [];
              return (
                <Box key={option.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 14, fontWeight: 700, color: '#333' }}
                    >
                      {option.text}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12, color: '#808080' }}>
                      {option.voteCount} {option.voteCount === 1 ? 'vote' : 'votes'}
                    </Typography>
                  </Box>
                  {voters.length === 0 ? (
                    <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, color: '#aaa' }}>
                      No votes yet
                    </Typography>
                  ) : (
                    voters.map(voter => (
                      <Box key={voter.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={getMediaUrl(voter.avatar)} alt={voter.name} sx={{ width: 28, height: 28 }} />
                        <Typography
                          sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, fontWeight: 500, color: '#333' }}
                        >
                          {voter.name}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
